/**
 * Right-click (context menu) support for Highcharts points.
 *
 * Highcharts exposes no point-level `contextmenu` event, so this listens on the
 * chart container, resolves which point is under the cursor and forwards it.
 * Used by the chart bases to open the comment popover on right-click, leaving
 * left-click free for zoom/selection.
 */
import type * as Highcharts from "highcharts";

type AnyChart = Highcharts.Chart & {
  hoverPoint?: Highcharts.Point;
  pointer?: { normalize?: (e: MouseEvent) => MouseEvent & { chartX: number } };
};

/**
 * Find the point under the pointer. Prefers Highcharts' own `hoverPoint` (kept
 * current as the mouse moves, and correct for shared-tooltip charts), and falls
 * back to a nearest-point search per visible series.
 */
function resolvePoint(
  chart: AnyChart,
  event: MouseEvent,
): Highcharts.Point | null {
  if (chart.hoverPoint) return chart.hoverPoint;

  try {
    const normalized = chart.pointer?.normalize?.(event);
    if (!normalized) return null;

    let best: Highcharts.Point | null = null;
    let bestDistance = Infinity;

    for (const series of chart.series ?? []) {
      const s = series as Highcharts.Series & {
        visible?: boolean;
        searchPoint?: (
          e: unknown,
          compareX: boolean,
        ) => Highcharts.Point | undefined;
      };
      if (s.visible === false || typeof s.searchPoint !== "function") continue;

      const candidate = s.searchPoint(normalized, true);
      if (!candidate) continue;

      const plotX = (candidate as unknown as { plotX?: number }).plotX ?? 0;
      const distance = Math.abs(plotX + chart.plotLeft - normalized.chartX);
      if (distance < bestDistance) {
        best = candidate;
        bestDistance = distance;
      }
    }

    return best;
  } catch {
    // Chart torn down mid-interaction, or a series without a kd-tree.
    return null;
  }
}

/**
 * Call `onPoint` when a chart point is right-clicked.
 *
 * The browser menu is suppressed only when a point is actually resolved, so a
 * right-click on empty chart space still behaves normally.
 *
 * @returns cleanup function that detaches the listener.
 */
export function attachPointContextMenu(
  chart: Highcharts.Chart | null | undefined,
  onPoint: (point: Highcharts.Point, event: MouseEvent) => void,
): () => void {
  const container = chart?.container;
  if (!chart || !container) return () => {};

  const handleContextMenu = (event: MouseEvent) => {
    const point = resolvePoint(chart as AnyChart, event);
    if (!point) return;
    event.preventDefault();
    // Keep the right-click away from Highcharts' own pointer handling, which
    // otherwise treats it as the start of a drag/selection and redraws the
    // chart when the gesture ends.
    event.stopPropagation();
    onPoint(point, event);
  };

  // Swallow the right button before Highcharts' pointer sees it, for the same
  // reason — `contextmenu` fires after mousedown, too late on its own.
  const swallowRightButton = (event: MouseEvent) => {
    if (event.button !== 2) return;
    if (!resolvePoint(chart as AnyChart, event)) return;
    event.stopPropagation();
  };

  // Capture phase so both run before Highcharts' listeners on the same element.
  container.addEventListener("contextmenu", handleContextMenu, true);
  container.addEventListener("mousedown", swallowRightButton, true);
  container.addEventListener("mouseup", swallowRightButton, true);

  return () => {
    container.removeEventListener("contextmenu", handleContextMenu, true);
    container.removeEventListener("mousedown", swallowRightButton, true);
    container.removeEventListener("mouseup", swallowRightButton, true);
  };
}
